import Reveal from "./Reveal";

const HANDS_IMG =
  "https://images.unsplash.com/photo-1585421514284-efb74c2b69ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxwZXJzb24lMjBjbGVhbmluZyUyMHdpcGluZ3xlbnwwfHx8fDE3ODY0MTQ3NDZ8MA&ixlib=rb-4.1.0&q=75&w=900&auto=format";
const OFFICE_IMG =
  "https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjbGVhbiUyMG9mZmljZXxlbnwwfHx8fDE3ODY0MTQ3NDV8MA&ixlib=rb-4.1.0&q=75&w=1600&auto=format";

const Card = ({ i, className = "", children, testId }) => (
  <Reveal
    i={i}
    data-testid={testId}
    className={`group relative overflow-hidden rounded-2xl border border-ink/10 bg-white p-8 transition-[box-shadow,transform] duration-500 hover:-translate-y-1 hover:shadow-[0_24px_60px_-24px_rgba(19,41,61,0.35)] lg:p-10 ${className}`}
  >
    {children}
  </Reveal>
);

const List = ({ items }) => (
  <ul className="mt-5 space-y-2 text-sm leading-relaxed text-slateblue">
    {items.map((t) => (
      <li key={t} className="flex gap-2.5">
        <span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-seafoam" />
        {t}
      </li>
    ))}
  </ul>
);

const CardTitle = ({ index, title, sub }) => (
  <div>
    <span className="font-serif text-sm italic text-seafoam">{index}</span>
    <h3 className="mt-1 font-serif text-2xl tracking-tight text-ink md:text-3xl">{title}</h3>
    {sub && <p className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-slateblue/70">{sub}</p>}
  </div>
);

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="mx-auto max-w-7xl px-6 py-24 lg:px-12 lg:py-36">
      <Reveal>
        <p className="eyebrow">What we do</p>
        <h2 className="mt-4 max-w-2xl font-serif text-4xl tracking-tight text-ink sm:text-5xl lg:text-6xl">
          Five simple ways we make <span className="italic text-ocean">cleaning</span> easy
        </h2>
      </Reveal>

      <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-6">
        <Card i={0} testId="service-card-standard" className="lg:col-span-4 lg:row-span-2">
          <div className="grid h-full gap-8 lg:grid-cols-2">
            <div>
              <CardTitle index="01" title="Standard Cleaning" sub="Weekly · Biweekly · Monthly" />
              <List
                items={[
                  "Kitchen — countertops, appliance exteriors, stovetop, cabinet fronts, trash",
                  "Bathrooms — toilet, tub & shower, sinks, mirrors, trash",
                  "Bedrooms & living areas — sweeping, mopping, dusting, vacuuming",
                ]}
              />
            </div>
            <div className="relative min-h-[220px] overflow-hidden rounded-xl">
              <img
                src={HANDS_IMG}
                alt="Hands carefully wiping a surface clean"
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
          </div>
        </Card>

        <Card i={1} testId="service-card-deep" className="lg:col-span-2 lg:row-span-2">
          <CardTitle index="02" title="Deep Cleaning" sub="First-time or seasonal" />
          <List
            items={[
              "Everything in Standard, plus detailed grout, cabinet & appliance-interior scrubbing",
              "Ceiling fans, light fixtures, door frames, trim & baseboards",
              "Want a little extra? Inside the oven, fridge or interior windows can be included — just mention it when you ask for your free quote",
            ]}
          />
        </Card>

        <Card i={2} testId="service-card-move" className="lg:col-span-2">
          <CardTitle index="03" title="Move-In / Move-Out" />
          <List
            items={[
              "Full inside/out clean of cabinets, appliances & closets",
              "Baseboards, sills, switches, interior windows & vents",
              "Helps secure your damage deposit back",
            ]}
          />
        </Card>

        <Card i={3} testId="service-card-construction" className="lg:col-span-2">
          <CardTitle index="04" title="Post-Construction" />
          <List
            items={[
              "Dust & debris removal from surfaces, vents and fixtures",
              "Cabinets, windows and floors detailed to remove fine dust",
              "Final wipe-down plus trash & debris haul-away",
            ]}
          />
        </Card>

        <Card i={4} testId="service-card-commercial" className="lg:col-span-2">
          <CardTitle index="05" title="Commercial Spaces & Offices" sub="Recurring · Daily / Weekly / Biweekly" />
          <List
            items={[
              "Workstations, common areas, break rooms & restrooms",
              "Entry glass spot-cleaned, supplies restocked",
            ]}
          />
        </Card>

        <Card i={5} testId="service-card-airbnb" className="lg:col-span-6">
          <div>
            <CardTitle index="06" title="B&B, Airbnb & Short-Term Rental Cleaning" sub="Turnover service" />
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slateblue">
              Reliable turnover cleaning for Airbnb, B&B and short-term rental hosts —
              thorough cleaning with attention to the details that create a great guest experience.
            </p>
            <div className="mt-5 grid gap-x-8 sm:grid-cols-2 lg:grid-cols-4">
              <List items={["Full turnover clean between guests"]} />
              <List items={["Kitchen, bathrooms & living areas refreshed"]} />
              <List items={["Linen & towel change on request"]} />
              <List items={["Scheduled around check-in / check-out"]} />
            </div>
          </div>
        </Card>

        <Reveal
          i={6}
          data-testid="service-card-cta"
          className="relative overflow-hidden rounded-2xl bg-ocean lg:col-span-6"
        >
          <img
            src={OFFICE_IMG}
            alt="A freshly cleaned modern office"
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover opacity-30"
          />
          <div className="relative z-10 flex flex-col items-start justify-between gap-6 p-8 md:flex-row md:items-center lg:p-12">
            <p className="max-w-xl font-serif text-2xl italic leading-snug tracking-tight text-salt md:text-3xl">
              Not sure which clean fits your space? Tell us about it — the quote is free.
            </p>
            <a
              href="#quote"
              data-testid="services-quote-button"
              className="shrink-0 rounded-full bg-salt px-8 py-4 text-sm font-bold uppercase tracking-widest text-ink transition-[background-color,transform] duration-300 hover:bg-seafoam hover:text-white hover:scale-[1.03]"
            >
              Get a Free Quote
            </a>
          </div>
        </Reveal>
      </div>

      <div className="mt-24">
        <Reveal>
          <p className="eyebrow">Beyond cleaning</p>
          <h3 className="mt-4 max-w-2xl font-serif text-3xl tracking-tight text-ink sm:text-4xl lg:text-5xl">
            Property & rental <span className="italic text-ocean">management</span>
          </h3>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-slateblue">
            Separate from our cleaning services, we also offer ongoing management for
            rental properties — tailored to what you and your property actually need.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-5 md:grid-cols-2">
          <Card i={1} testId="service-card-airbnb-management">
            <CardTitle index="07" title="Airbnb Management" sub="For short-term rental hosts" />
            <p className="mt-4 text-sm leading-relaxed text-slateblue">
              Want a more hands-off rental? Alongside turnover cleaning, we can take on the
              agreed hosting responsibilities for your Airbnb or short-term rental — shaped
              around your property and how much support you want.
            </p>
            <p className="mt-5 inline-block rounded-full bg-mist px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] text-ocean" data-testid="airbnb-mgmt-fee-note">
              Flexible &amp; Negotiable Management Fees — Percentage or Fixed-Fee Options Available
            </p>
          </Card>

          <Card i={2} testId="service-card-property-management">
            <CardTitle index="08" title="Property Management" sub="Ongoing care for your property" />
            <p className="mt-4 text-sm leading-relaxed text-slateblue">
              More than cleaning: we take care of your property and coordinate the agreed-upon,
              property-related responsibilities for a specific period of time — based on your
              needs and the services we agree on together.
            </p>
            <p className="mt-5 inline-block rounded-full bg-mist px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] text-ocean" data-testid="property-mgmt-fee-note">
              Flexible &amp; Negotiable Management Fees — Percentage or Fixed-Fee Options Available
            </p>
          </Card>
        </div>
      </div>
    </section>
  );
}
